import { createContext, useContext } from 'react'

export const API_URL = 'https://api.nexatice.dev'

export interface LicenseInfo {
  key: string
  type: 'monthly'
  status: string
  expiresAt: string | null
  machineCount: number
  maxMachines: number
}

export interface PurchaseHistory {
  id: string
  date: string
  description: string
  amount: number
  currency: string
  status: string
  licenseKey: string
}

export interface User {
  id: number
  email: string
  subscriptionStatus: string
  license?: LicenseInfo | null
  licenses?: LicenseInfo[]
  purchases?: PurchaseHistory[]
}

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

export interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>
  register: (email: string, password: string) => Promise<{ success: boolean; message: string }>
  logout: () => Promise<void>
  refreshAuth: () => Promise<boolean>
  forgotPassword: (email: string) => Promise<{ success: boolean; message: string }>
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; message: string }>
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message: string }>
  linkLicense: (licenseKey: string) => Promise<{ success: boolean; message: string }>
  clearError: () => void
}

export const AuthContext = createContext<AuthContextType | null>(null)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

const TOKEN_KEY = 'nexatice_refresh_token'
let accessToken: string | null = null

export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(TOKEN_KEY)
}

export function setRefreshToken(token: string | null): void {
  if (typeof window === 'undefined') return
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_KEY)
  }
}

export function getAccessToken(): string | null {
  return accessToken
}

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {})
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include'
  })

  const data = await response.json()
  return data as T
}

export async function authLogin(email: string, password: string): Promise<{
  success: boolean
  message: string
  user?: User
  accessToken?: string
  refreshToken?: string
}> {
  const result = await apiRequest<{
    success: boolean
    message: string
    user?: User
    accessToken?: string
    refreshToken?: string
  }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  })

  if (result.success && result.accessToken && result.refreshToken) {
    setAccessToken(result.accessToken)
    setRefreshToken(result.refreshToken)
  }

  return result
}

export async function authRegister(email: string, password: string): Promise<{
  success: boolean
  message: string
  user?: User
  accessToken?: string
  refreshToken?: string
}> {
  const result = await apiRequest<{
    success: boolean
    message: string
    user?: User
    accessToken?: string
    refreshToken?: string
  }>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  })

  if (result.success && result.accessToken && result.refreshToken) {
    setAccessToken(result.accessToken)
    setRefreshToken(result.refreshToken)
  }

  return result
}

export async function authRefresh(): Promise<{
  success: boolean
  accessToken?: string
}> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    return { success: false }
  }

  const result = await apiRequest<{
    success: boolean
    accessToken?: string
    message?: string
  }>('/api/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken })
  })

  if (result.success && result.accessToken) {
    setAccessToken(result.accessToken)
  } else {
    setRefreshToken(null)
    setAccessToken(null)
  }

  return result
}

export async function authLogout(): Promise<void> {
  const refreshToken = getRefreshToken()
  if (refreshToken) {
    try {
      await apiRequest('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken })
      })
    } catch {
    }
  }
  setAccessToken(null)
  setRefreshToken(null)
}

export async function authStatus(): Promise<{
  authenticated: boolean
  user?: User
  message?: string
}> {
  return apiRequest<{
    authenticated: boolean
    user?: User
    message?: string
  }>('/api/auth/status')
}

export async function authForgotPassword(email: string): Promise<{
  success: boolean
  message: string
}> {
  return apiRequest<{
    success: boolean
    message: string
  }>('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email })
  })
}

export async function authResetPassword(token: string, password: string): Promise<{
  success: boolean
  message: string
}> {
  return apiRequest<{
    success: boolean
    message: string
  }>('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password })
  })
}

export async function authChangePassword(currentPassword: string, newPassword: string): Promise<{
  success: boolean
  message: string
}> {
  return apiRequest<{
    success: boolean
    message: string
  }>('/api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword })
  })
}

export async function authLinkLicense(licenseKey: string): Promise<{
  success: boolean
  message: string
  license?: LicenseInfo
}> {
  return apiRequest<{
    success: boolean
    message: string
    license?: LicenseInfo
  }>('/api/auth/link-license', {
    method: 'POST',
    body: JSON.stringify({ licenseKey })
  })
}

export async function fetchUserLicenses(): Promise<{
  success: boolean
  licenses: LicenseInfo[]
}> {
  return apiRequest<{
    success: boolean
    licenses: LicenseInfo[]
  }>('/api/auth/licenses')
}

export async function fetchPurchaseHistory(): Promise<{
  success: boolean
  purchases: PurchaseHistory[]
}> {
  return apiRequest<{
    success: boolean
    purchases: PurchaseHistory[]
  }>('/api/auth/purchases')
}

